import { getDateStr } from '@/utils/dates'
import { getPartnerViewIdsForGroups, getSiteIdsForGroups } from '@/utils/store'

const now = new Date()
const TRAFFIC_SOURCE_STORAGE_KEY = 'dashboard_v2_traffic_source'
const CACHE_MAX_AGE = 5 * 60 * 1000

export const DEFAULT_TRAFFIC_SOURCE = 'wysistat'

export const DEFAULT_COMPARISON = {
  prevLabel: '', yearLabel: '',
  prev: null, prevPercent: null, prevTrend: '',
  year: null, yearPercent: null, yearTrend: '',
}

// Only cache responses for past periods — current-month data is updated by scheduled jobs.
export const buildCacheOptions = (dateTo) => {
  const currentMonth = new Date().toISOString().substring(0, 7)
  return dateTo?.substring(0, 7) < currentMonth ? { cache: { maxAge: CACHE_MAX_AGE } } : {}
}

export const buildKpiDateParams = (view, dateFrom, dateTo) => {
  if (view === 'year') return { year: Number(dateFrom?.substring(0, 4)) }
  if (view === 'month') return { month: dateFrom?.substring(0, 7) }
  return { date_start: dateFrom, date_end: dateTo }
}

export const buildScopeParams = (state, includeSource = true) => ({
  ...(state.sites.length > 0 ? { sites: state.sites.join(',') } : {}),
  ...(state.partnerViews.length > 0 ? { partner_views: state.partnerViews.join(',') } : {}),
  ...(includeSource ? { source: state.trafficSource || DEFAULT_TRAFFIC_SOURCE } : {}),
})

const normalizeTrafficSource = (source) => (source === 'ga4' ? 'ga4' : DEFAULT_TRAFFIC_SOURCE)

const getStoredTrafficSource = () => {
  try {
    return normalizeTrafficSource(localStorage.getItem(TRAFFIC_SOURCE_STORAGE_KEY))
  } catch (_) {
    return DEFAULT_TRAFFIC_SOURCE
  }
}

const DEFAULT_STATE = {
  // Traffic source (wysistat or ga4)
  trafficSource: getStoredTrafficSource(),

  // Date range
  dateFrom: getDateStr(new Date(now.getFullYear(), now.getMonth(), 1)),
  dateTo: getDateStr(now),

  // Filter selections
  partnerViewGroups: [],
  partnerViews: [],
  siteGroups: [],
  sites: [],

  // Fetched and cached data for all three tabs
  overviewKpiData: null,
  overviewComparisonsLoaded: false,
  analyticsData: null,
  performanceData: null,
  refreshGeneration: 0,
}

export default {
  namespaced: true,

  state: { ...DEFAULT_STATE },

  getters: {
    getTrafficSource:     (state) => state.trafficSource || DEFAULT_TRAFFIC_SOURCE,
    getDateFrom:          (state) => state.dateFrom,
    getDateTo:            (state) => state.dateTo,
    getMonth:             (state) => (state.dateFrom ? state.dateFrom.substring(0, 7) : ''),
    getPartnerViewGroups: (state) => state.partnerViewGroups,
    getPartnerViews:      (state) => state.partnerViews,
    getSiteGroups:        (state) => state.siteGroups,
    getSites:             (state) => state.sites,
    getOverviewKpiData:   (state) => state.overviewKpiData,
    getOverviewComparisonsLoaded: (state) => state.overviewComparisonsLoaded,
    getAnalyticsData:     (state) => state.analyticsData,
    getPerformanceData:   (state) => state.performanceData,
    getRefreshGeneration: (state) => state.refreshGeneration,
  },

  mutations: {
    incrementRefreshGeneration(state) { state.refreshGeneration += 1 },
    setTrafficSource(state, source) {
      const normalized = normalizeTrafficSource(source)
      state.trafficSource = normalized
      try {
        localStorage.setItem(TRAFFIC_SOURCE_STORAGE_KEY, normalized)
      } catch (_) {
        // Ignore storage errors
      }
    },
    setDateFrom(state, date) { state.dateFrom = date },
    setDateTo(state, date) { state.dateTo = date },
    setPartnerViewGroups(state, groups) { state.partnerViewGroups = groups },
    setPartnerViews(state, views) { state.partnerViews = views },
    setSiteGroups(state, groups) { state.siteGroups = groups },
    setSites(state, sites) { state.sites = sites },
    setOverviewKpiData(state, data) { state.overviewKpiData = data },
    setOverviewComparisonsLoaded(state, value) { state.overviewComparisonsLoaded = value },
    setAnalyticsData(state, data) { state.analyticsData = data },
    setPerformanceData(state, data) { state.performanceData = data },
  },

  actions: {
    async updatePartnerViewGroups({ commit }, groupIds) {
      commit('setPartnerViewGroups', groupIds)
      if (!groupIds || groupIds.length === 0) {
        commit('setPartnerViews', [])
        return
      }
      const views = await getPartnerViewIdsForGroups(groupIds)
      commit('setPartnerViews', views)
    },

    async updateSiteGroups({ commit }, groupIds) {
      commit('setSiteGroups', groupIds)
      if (!groupIds || groupIds.length === 0) {
        commit('setSites', [])
        return
      }
      const sites = await getSiteIdsForGroups(groupIds)
      commit('setSites', sites)
    },

    updateSites({ commit }, siteIds) {
      commit('setSites', siteIds)
    },

    clearFilters({ commit }) {
      commit('setPartnerViewGroups', [])
      commit('setPartnerViews', [])
      commit('setSiteGroups', [])
      commit('setSites', [])
    },
  },
}
